import React from "react";
import { RouteComponentProps } from "react-router";
import { request, handleValidationErrors } from "../helpers/utilities";
import { Spin, message } from "antd";
import Card from "../components/Card";
import queryString from "query-string";
import { AxiosResponse } from "axios";
import { FormInstance } from "antd/lib/form/Form";
import BaseForm from "../components/Form/BaseForm";
import { FormFieldConfig, APIError } from "../helpers/shared-interfaces";
import * as Sentry from "@sentry/browser";
import { GET_TRUSTEE_SCHEME, UPDATE_TRUSTEE_SCHEME } from "../gql/trustees";
import { generateDisplayErrorMessage } from "../helpers/utilities";

interface State {
  name: string;
  id: string | number;
  isLoading: boolean;
}

interface RouteParams {
  id: string;
  schemeId: string;
}

export default class EditTrusteeScheme extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference = React.createRef<FormInstance>();

  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = { name: "", isLoading: true, id: "" };
    this.redirectToTrusteeSchemePage =
      this.redirectToTrusteeSchemePage.bind(this);
    this.updateTrusteeScheme = this.updateTrusteeScheme.bind(this);
    this.getTrusteeScheme = this.getTrusteeScheme.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  async updateTrusteeScheme(): Promise<void> {
    interface Response {
      data?: {
        updateTrusteeScheme?: {
          trusteeScheme?: { name: string };
          errors?: APIError[];
        };
      };
      errors?: Record<string, unknown>[];
    }

    if (!this.reference.current) return;

    let data;
    try {
      data = await this.reference.current.validateFields(["name"]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_TRUSTEE_SCHEME,
        variables: { id: this.state.id, name: data.name },
      });

      const updated = response?.data?.data?.updateTrusteeScheme;

      if (updated?.trusteeScheme) {
        message.success("Trustee scheme updated successfully");
        this.redirectToTrusteeSchemePage();
      } else {
        const description = updated?.errors?.[0]?.description;
        message.error(
          description
            ? `Failed to update trustee scheme - ${description}`
            : "Failed to update trustee scheme",
        );
      }
    } catch (error) {
      message.error(
        `Failed to update trustee scheme - ${generateDisplayErrorMessage(
          error,
        )}`,
      );
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  redirectToTrusteeSchemePage(): void {
    this.props.history.push(
      `/trustees/${this.props.match.params.id}/schemes/${this.state.id}`,
    );
  }

  getTrusteeScheme = async (): Promise<void> => {
    this.setState({ isLoading: true });

    try {
      const { data }: AxiosResponse<any> = await request({
        query: GET_TRUSTEE_SCHEME,
        variables: { id: this.state.id },
      });

      this.setState({ isLoading: false });

      if (data?.errors) {
        message.error("Failed to fetch scheme due to server errors.");
        return;
      }

      const scheme = data?.data?.trusteeScheme;

      if (!scheme) {
        message.error("Scheme not found.");
        return;
      }

      this.setState({ name: scheme.name }, () => {
        this.reference.current?.setFieldsValue({ name: scheme.name });
      });
    } catch (error) {
      message.error(
        `An error occurred while fetching scheme. - ${generateDisplayErrorMessage(
          error,
        )}`,
      );
      this.setState({ isLoading: false });
    }
  };

  componentDidMount(): void {
    const parsed = queryString.parse(window.location.search);
    const parsedObject: { name: string } = JSON.parse(JSON.stringify(parsed));

    this.setState(
      { name: parsedObject.name, id: this.props.match.params.schemeId },
      () => {
        this.getTrusteeScheme();
      },
    );
  }

  render(): JSX.Element | null {
    const { name, isLoading } = this.state;

    const fields: FormFieldConfig[] = [
      {
        name: "name",
        label: "Scheme Name",
        inputType: "string",
        placeholder: "Enter Trustee Scheme Name ...",
        rule: "string",
        initialValue: name,
        required: true,
        colConfig: { span: 6 },
      },
      {
        name: "updateTrusteeSchemeButton",
        inputType: "button",
        value: "Save",
        required: true,
        className: "base-button",
        elementStyle: { position: "relative", top: "10px" },
        colConfig: { span: 23 },
        handleClick: this.updateTrusteeScheme,
      },
    ];

    return (
      <Card header="">
        <div className="edit-trustee-scheme-container">
          <Spin spinning={isLoading}>
            <BaseForm
              size="large"
              reference={this.reference}
              layout="vertical"
              fieldsToHide={[]}
              fields={fields}
            />
          </Spin>
        </div>
      </Card>
    );
  }
}
